import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Popover, useTheme } from '@mui/material';
import { SELECT_LIST_MAX_HEIGHT, useStyles } from './styles';

export type SearchableMultiSelectItem = {
  id: string;
};

type SearchableMultiSelectProps<T extends SearchableMultiSelectItem> = {
  items: T[] | undefined;
  selectedIds: string[];
  onChange: (nextIds: string[]) => void;
  getLabel: (item: T) => string;
  /** Shown on the right of each option. Omit to hide the badge. */
  getBadge?: (item: T) => string;
  /** `query` is already trimmed and lowercased. */
  matchesQuery: (item: T, query: string) => boolean;
  placeholder: string;
  emptyLabel: string;
  getRemoveLabel: (label: string) => string;
  listboxId: string;
  disabled?: boolean;
  portalDropdown?: boolean;
};

const MIN_LIST_HEIGHT = 120;

export function SearchableMultiSelect<T extends SearchableMultiSelectItem>({
  items,
  selectedIds,
  onChange,
  getLabel,
  getBadge,
  matchesQuery,
  placeholder,
  emptyLabel,
  getRemoveLabel,
  listboxId,
  disabled,
  portalDropdown = false,
}: SearchableMultiSelectProps<T>) {
  const theme = useTheme();
  const { classes } = useStyles();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const controlRef = useRef<HTMLDivElement | null>(null);
  const dropdownPortalRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const ignoreOutsideCloseRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [listMaxHeight, setListMaxHeight] = useState(SELECT_LIST_MAX_HEIGHT);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const itemById = useMemo(() => {
    const map = new Map<string, T>();
    (items ?? []).forEach((item) => map.set(item.id, item));
    return map;
  }, [items]);

  const selectedItems = useMemo(
    () => selectedIds.map((id) => itemById.get(id)).filter((item): item is T => item !== undefined),
    [selectedIds, itemById],
  );

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items ?? [];
    return (items ?? []).filter((item) => matchesQuery(item, q));
  }, [items, matchesQuery, query]);

  const showInputPlaceholder = selectedItems.length === 0 && !query;

  const closeDropdown = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  };

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
  };

  const updateListMaxHeight = useCallback(() => {
    if (!controlRef.current) return;
    const rect = controlRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 16;
    setListMaxHeight(Math.max(MIN_LIST_HEIGHT, Math.min(SELECT_LIST_MAX_HEIGHT, spaceBelow)));
  }, []);

  useEffect(() => {
    if (!open) return;
    updateListMaxHeight();
    window.addEventListener('resize', updateListMaxHeight);
    window.addEventListener('scroll', updateListMaxHeight, true);
    return () => {
      window.removeEventListener('resize', updateListMaxHeight);
      window.removeEventListener('scroll', updateListMaxHeight, true);
    };
  }, [open, updateListMaxHeight]);

  useEffect(() => {
    if (filteredItems.length === 0) {
      setActiveIndex(-1);
      return;
    }
    if (activeIndex >= filteredItems.length || activeIndex < 0) {
      setActiveIndex(0);
    }
  }, [filteredItems, activeIndex]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (ignoreOutsideCloseRef.current) return;
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (dropdownPortalRef.current?.contains(target)) return;
      closeDropdown();
    };
    const listenerId = window.setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown);
    }, 0);
    return () => {
      window.clearTimeout(listenerId);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  const toggleItem = (itemId: string) => {
    if (selectedSet.has(itemId)) {
      onChange(selectedIds.filter((id) => id !== itemId));
      return;
    }
    onChange([...selectedIds, itemId]);
    setQuery('');
    inputRef.current?.focus();
  };

  const removeItem = (event: React.MouseEvent, itemId: string) => {
    event.stopPropagation();
    onChange(selectedIds.filter((id) => id !== itemId));
    inputRef.current?.focus();
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDropdown();
      inputRef.current?.blur();
      return;
    }
    if (event.key === 'Backspace' && !query && selectedIds.length > 0) {
      onChange(selectedIds.slice(0, -1));
      return;
    }
    if (!filteredItems.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((prev) => (prev + 1) % filteredItems.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0) toggleItem(filteredItems[activeIndex].id);
    }
  };

  const handleControlMouseDown = (event: React.MouseEvent) => {
    if (disabled) return;
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    ignoreOutsideCloseRef.current = true;
    openDropdown();
    inputRef.current?.focus();
    window.requestAnimationFrame(() => {
      ignoreOutsideCloseRef.current = false;
    });
  };

  const listContent = (
    <Box
      id={listboxId}
      role="listbox"
      className={classes.list}
      aria-multiselectable
      style={{ maxHeight: listMaxHeight }}>
      {filteredItems.length === 0 ? (
        <Box className={classes.empty}>{emptyLabel}</Box>
      ) : (
        filteredItems.map((item, idx) => {
          const isSelected = selectedSet.has(item.id);
          const isActive = activeIndex === idx;
          return (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={isSelected}
              className={classNames(classes.row, {
                [classes.rowActive]: isActive && !isSelected,
                [classes.rowSelected]: isSelected,
              })}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => toggleItem(item.id)}>
              <span className={classes.rowTitle}>{getLabel(item)}</span>
              {getBadge ? <span className={classes.providerBadge}>{getBadge(item)}</span> : null}
              {isSelected ? <CheckIcon className={classes.checkIcon} /> : null}
            </button>
          );
        })
      )}
    </Box>
  );

  return (
    <Box
      ref={rootRef}
      className={classNames(classes.root, { [classes.rootOpen]: open && !portalDropdown })}>
      <Box
        ref={controlRef}
        className={classNames(classes.control, {
          [classes.controlFocused]: open,
          [classes.controlDisabled]: disabled,
        })}
        onMouseDown={handleControlMouseDown}>
        {selectedItems.length > 0 ? (
          <Box className={classes.chipsScroll}>
            {selectedItems.map((item) => {
              const label = getLabel(item);
              return (
                <span key={item.id} className={classes.chip}>
                  <span className={classes.chipLabel}>{label}</span>
                  <button
                    type="button"
                    className={classes.chipRemove}
                    disabled={disabled}
                    aria-label={getRemoveLabel(label)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => removeItem(event, item.id)}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </button>
                </span>
              );
            })}
          </Box>
        ) : null}
        <Box className={classes.inputRow}>
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            disabled={disabled}
            className={classes.inlineInput}
            placeholder={showInputPlaceholder ? placeholder : undefined}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              openDropdown();
            }}
            onFocus={openDropdown}
            onKeyDown={onInputKeyDown}
          />
        </Box>
        <span className={classes.chevron}>
          <KeyboardArrowDownIcon fontSize="small" />
        </span>
      </Box>

      {open && !disabled && portalDropdown ? (
        <Popover
          open
          anchorEl={controlRef.current}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          marginThreshold={0}
          slotProps={{
            root: {
              sx: { zIndex: theme.zIndex.modal + 1 },
            },
            paper: {
              className: classes.dropdownPortalPaper,
              style: { width: controlRef.current?.offsetWidth },
            },
          }}>
          <Box
            ref={dropdownPortalRef}
            onMouseDown={(event) => event.preventDefault()}
            sx={{ width: '100%' }}>
            {listContent}
          </Box>
        </Popover>
      ) : null}

      {open && !disabled && !portalDropdown ? (
        <Box className={classes.dropdown} onMouseDown={(event) => event.preventDefault()}>
          {listContent}
        </Box>
      ) : null}
    </Box>
  );
}
